import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * The one "are you sure you want to complete this?" dialog.
 *
 * Every completion confirmation in the app renders through here — the project
 * checkbox on the Projects page and the account page, the auto-close prompt when
 * the last task of a project is completed, and the task / sub-task checkboxes in
 * TaskDetailRow. They were separately hand-written before, which is how the
 * account page's copy silently went stale when the close cascade moved
 * server-side, and how two of them ended up with different button labels.
 *
 * The dialog only asks the question. WHICH clicks are worth asking about, and
 * what the consequences are, come from the caller via `lines`
 * (`buildProjectCloseWarning` / `buildTaskCompleteWarning`); an empty `lines` is
 * a plain confirmation with no consequences to report.
 */
export function CompleteConfirmDialog({
  open,
  onOpenChange,
  subject,
  name,
  lines,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subject: "project" | "task" | "sub-task";
  name?: string | null;
  lines: string[];
  onConfirm: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Mark this {subject} complete?</AlertDialogTitle>
          <AlertDialogDescription>
            {name
              ? <><span className="font-medium text-foreground">{name}</span> will be marked complete.</>
              : <>This {subject} will be marked complete.</>}
            {lines.map((line) => <span key={line}> {line}</span>)}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>Mark complete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
